/**
 * CompanyService - 陸送会社管理サービス
 * ラクハイ管理者向けの陸送会社CRUD操作を提供
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { Company, User, Role } from '../types';
import { getSupabaseClient } from '../utils/supabase';
import { DataServiceError, ErrorCodes } from './dataService';

export interface CompanyCreateData {
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  subscriptionPlan?: string;
}

export interface CompanyUpdateData extends Partial<CompanyCreateData> {
  subscriptionStatus?: 'active' | 'inactive' | 'trial' | 'suspended';
  defaultPageSize?: number; // FL-134: ページング設定
}

export interface CompanyStats {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
  activeUsers: number;
  companiesCreatedThisMonth: number;
}

export class CompanyService {
  private supabase: SupabaseClient | null;

  constructor() {
    this.supabase = getSupabaseClient();
  }

  /**
   * Supabaseクライアントの可用性チェック
   */
  private ensureSupabaseClient(): SupabaseClient {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
    return this.supabase;
  }

  /**
   * 陸送会社一覧取得（ラクハイ管理者のみ）
   */
  async getCompanies(filters?: {
    status?: string;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ companies: Company[]; totalCount: number }> {
    try {
      const supabase = this.ensureSupabaseClient();
      let query = supabase.from('companies').select('*', { count: 'exact' });

      // フィルタリング
      if (filters?.status) {
        query = query.eq('subscription_status', filters.status);
      }

      if (filters?.searchQuery) {
        query = query.or(
          `name.ilike.%${filters.searchQuery}%,code.ilike.%${filters.searchQuery}%,email.ilike.%${filters.searchQuery}%`
        );
      }

      // ソート・ページネーション
      query = query.order('created_at', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
      }

      const { data, error, count } = await query;

      if (error) {
        throw new DataServiceError(
          `陸送会社一覧取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      return {
        companies: data || [],
        totalCount: count || 0,
      };
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社一覧取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社詳細取得
   */
  async getCompanyById(id: string): Promise<Company | null> {
    try {
      const supabase = this.ensureSupabaseClient();
      const { data, error } = await supabase.from('companies').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw new DataServiceError(
          `陸送会社取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社名から会社コードを自動生成
   * @param companyName 会社名
   * @returns ユニークな会社コード
   */
  private async generateCompanyCode(companyName: string): Promise<string> {
    const supabase = this.ensureSupabaseClient();

    // 会社名から英数字のみ抽出して大文字化
    const alphanumeric = companyName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    let baseCode: string;
    if (alphanumeric.length >= 6) {
      // 最初の6文字（または全文字）を使用
      baseCode = alphanumeric.substring(0, Math.min(6, alphanumeric.length));
    } else if (alphanumeric.length > 0) {
      // 短い場合はランダム3桁数字を追加
      const randomNum = Math.floor(Math.random() * 900) + 100; // 100-999
      baseCode = alphanumeric + randomNum.toString();
    } else {
      // 英数字が抽出できない場合はデフォルト
      baseCode = 'COMPANY';
    }

    // ユニークなコードになるまでリトライ（最大10回）
    for (let i = 0; i < 10; i++) {
      const code = i === 0 ? baseCode : `${baseCode}${String(i).padStart(2, '0')}`; // 01, 02, ...

      // 重複チェック
      const { data } = await supabase.from('companies').select('id').eq('code', code).maybeSingle();

      if (!data) {
        return code; // ユニーク
      }
    }

    // 最終手段：ランダム6桁数字（3回まで重複チェック）
    for (let retry = 0; retry < 3; retry++) {
      const randomCode = 'COMPANY' + Math.floor(Math.random() * 900000 + 100000);
      const { data } = await supabase
        .from('companies')
        .select('id')
        .eq('code', randomCode)
        .maybeSingle();

      if (!data) {
        return randomCode; // ユニーク
      }
    }

    // すべて失敗した場合はエラー
    throw new DataServiceError(
      '会社コードの生成に失敗しました。時間をおいて再度お試しください。',
      ErrorCodes.DATA_CREATION_FAILED
    );
  }

  /**
   * 陸送会社作成（ラクハイ管理者のみ）
   */
  async createCompany(companyData: CompanyCreateData): Promise<Company> {
    try {
      const supabase = this.ensureSupabaseClient();

      // コードが空の場合は自動生成、入力されている場合はそのまま使用
      const code =
        companyData.code && companyData.code.trim()
          ? companyData.code.trim()
          : await this.generateCompanyCode(companyData.name);

      // 手動入力された場合は重複チェック
      if (companyData.code && companyData.code.trim()) {
        const { data: existingCompany, error } = await supabase
          .from('companies')
          .select('id')
          .eq('code', code)
          .maybeSingle();

        if (error) {
          throw new DataServiceError(
            `会社コード重複チェックに失敗しました: ${error.message}`,
            ErrorCodes.DATA_ACCESS_ERROR
          );
        }

        if (existingCompany) {
          throw new DataServiceError(
            '指定された会社コードは既に使用されています',
            ErrorCodes.VALIDATION_ERROR
          );
        }
      }

      const { data, error } = await supabase
        .from('companies')
        .insert({
          name: companyData.name,
          code: code,
          address: companyData.address,
          phone: companyData.phone,
          email: companyData.email,
          contact_person: companyData.contactPerson,
          subscription_plan: companyData.subscriptionPlan || 'basic',
          subscription_status: 'active',
          settings: {},
        })
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `陸送会社作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社作成に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 全陸送会社一覧取得
   */
  async getAllCompanies(): Promise<Company[]> {
    try {
      const supabase = this.ensureSupabaseClient();
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `陸送会社一覧取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      // FL-184: DBのsnake_caseをフロントエンドのcamelCaseに変換
      return (data || []).map((company: any) => ({
        id: company.id,
        name: company.name,
        code: company.code || company.id,
        address: company.address,
        phone: company.phone,
        email: company.email,
        contactPerson: company.contact_person,
        subscriptionPlan: company.subscription_plan || 'basic',
        subscriptionStatus: company.subscription_status || 'active',
        maxUsers: company.max_users || 10,
        maxDrivers: company.max_drivers || 5,
        maxVehicles: company.max_vehicles || 10,
        settings: company.settings || {},
        createdAt: company.created_at,
        updatedAt: company.updated_at,
      }));
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社一覧取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社更新（ラクハイ管理者のみ）
   */
  async updateCompany(id: string, updates: CompanyUpdateData): Promise<Company> {
    try {
      // 会社コード重複チェック（更新時）
      const supabase = this.ensureSupabaseClient();
      if (updates.code) {
        const { data: existingCompany, error } = await supabase
          .from('companies')
          .select('id')
          .eq('code', updates.code)
          .neq('id', id)
          .maybeSingle();

        if (error) {
          throw new DataServiceError(
            `会社コード重複チェックに失敗しました: ${error.message}`,
            ErrorCodes.DATA_ACCESS_ERROR
          );
        }

        if (existingCompany) {
          throw new DataServiceError(
            '指定された会社コードは既に使用されています',
            ErrorCodes.VALIDATION_ERROR
          );
        }
      }

      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      // マッピング処理
      if (updates.name) updateData.name = updates.name;
      if (updates.code) updateData.code = updates.code;
      if (updates.address !== undefined) updateData.address = updates.address;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.contactPerson !== undefined) updateData.contact_person = updates.contactPerson;
      if (updates.subscriptionPlan) updateData.subscription_plan = updates.subscriptionPlan;
      if (updates.subscriptionStatus) updateData.subscription_status = updates.subscriptionStatus;

      const { data, error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        throw new DataServiceError(
          `陸送会社更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      if (!data) {
        throw new DataServiceError('会社が見つかりません', ErrorCodes.RESOURCE_NOT_FOUND);
      }

      return data;
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社更新に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社削除（ラクハイ管理者のみ - ソフトデリート）
   */
  async deleteCompany(id: string): Promise<void> {
    try {
      // アクティブユーザーが存在するかチェック
      const supabase = this.ensureSupabaseClient();
      const { data: activeUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('company_id', id)
        .limit(1);

      if (usersError) {
        throw new DataServiceError(
          `ユーザー確認に失敗しました: ${usersError.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      if (activeUsers && activeUsers.length > 0) {
        throw new DataServiceError(
          'この陸送会社にはアクティブなユーザーが存在するため削除できません。まずユーザーを削除してください。',
          ErrorCodes.VALIDATION_ERROR
        );
      }

      // ソフトデリート（subscription_statusをinactiveに変更）
      const { error } = await supabase
        .from('companies')
        .update({
          subscription_status: 'inactive',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        throw new DataServiceError(
          `陸送会社削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '陸送会社削除に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 統計情報取得（ラクハイ管理者向けダッシュボード）
   */
  async getCompanyStats(): Promise<CompanyStats> {
    try {
      // 並行実行で各種統計を取得
      const supabase = this.ensureSupabaseClient();
      const [companiesResult, activeCompaniesResult, usersResult, thisMonthCompaniesResult] =
        await Promise.all([
          supabase.from('companies').select('id', { count: 'exact', head: true }),
          supabase
            .from('companies')
            .select('id', { count: 'exact', head: true })
            .eq('subscription_status', 'active'),
          supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
          supabase
            .from('companies')
            .select('id', { count: 'exact', head: true })
            .gte(
              'created_at',
              new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
            ),
        ]);

      const stats = {
        totalCompanies: companiesResult.count || 0,
        activeCompanies: activeCompaniesResult.count || 0,
        totalUsers: usersResult.count || 0,
        activeUsers: usersResult.count || 0, // TODO: activeユーザーの定義を決めて実装
        companiesCreatedThisMonth: thisMonthCompaniesResult.count || 0,
      };

      return stats;
    } catch (error) {
      throw new DataServiceError(
        '統計情報取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社のユーザー一覧取得
   */
  async getCompanyUsers(companyId: string): Promise<User[]> {
    try {
      const supabase = this.ensureSupabaseClient();
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `会社ユーザー一覧取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      return (data || []).map(profile => ({
        id: profile.auth_user_id || crypto.randomUUID(),
        name: profile.name,
        role: profile.role as Role,
        company: companyId, // TODO: 実際の会社名を取得
        companyId: profile.company_id,
        landTransporterId: profile.land_transporter_id,
      }));
    } catch (error) {
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '会社ユーザー一覧取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送会社のシステム利用可否を更新（FL-184）
   * subscription_statusを使用してシステム利用可否を制御
   * @param companyId - 会社ID
   * @param isActive - システム利用可否（true: active, false: inactive）
   * @param currentUser - 現在のユーザー（権限チェック用）
   */
  async updateCompanyActiveStatus(
    companyId: string,
    isActive: boolean,
    currentUser?: User
  ): Promise<void> {
    // スーパー管理者権限チェック
    if (currentUser && currentUser.role !== Role.SUPER_ADMIN) {
      throw new DataServiceError(
        'この操作にはスーパー管理者権限が必要です',
        ErrorCodes.PERMISSION_DENIED
      );
    }

    // subscription_statusを更新（active or inactive）
    const subscriptionStatus = isActive ? 'active' : 'inactive';
    await this.updateCompany(companyId, { subscriptionStatus });
  }
}
